import { CURRENCIES, DEFAULT_CURRENCY, formatMoney, toMinorUnits } from "@vouch/shared";
import { Check, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PeriodFilter } from "../components/PeriodFilter";
import { Button, Card, Input, Modal, Select, Spinner } from "../components/ui";
import { useAuth } from "../lib/auth";
import { confirm } from "../lib/confirm";
import {
  useAccounts,
  useConnections,
  useCreateLoan,
  useFxRate,
  useLoans,
  useRepayLoan,
  useRequestUndo,
  useRespondLoan,
  useRespondRepayment,
  useRespondUndo,
  type Account,
  type CreateLoanBody,
  type Loan,
  type Period,
} from "../lib/hooks";
import { toast } from "../lib/toast";

export function LoansPage(): JSX.Element {
  const { user } = useAuth();
  const [period, setPeriod] = useState<Period>({});
  const { data: loans, isLoading } = useLoans(period);
  const { data: accounts = [] } = useAccounts();
  const createLoan = useCreateLoan();
  const repay = useRepayLoan();
  const respond = useRespondLoan();
  const respondRepayment = useRespondRepayment();
  const requestUndo = useRequestUndo();
  const respondUndo = useRespondUndo();
  const [open, setOpen] = useState(false);
  const [repayFor, setRepayFor] = useState<Loan | null>(null);
  const [confirmRepayFor, setConfirmRepayFor] = useState<Loan | null>(null);
  const [acceptFor, setAcceptFor] = useState<Loan | null>(null);
  const [counterpartyId, setCounterpartyId] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // The "other person" on each loan, deduped — drives the counterparty filter.
  const counterpartyOf = (loan: Loan): { id: string; name: string } => {
    const lent = loan.lenderUserId === user?.id;
    return {
      id: lent ? loan.borrowerUserId : loan.lenderUserId,
      name: (lent ? loan.borrowerName : loan.lenderName) ?? "someone",
    };
  };
  const counterparties = useMemo(() => {
    const map = new Map<string, string>();
    for (const loan of loans ?? []) {
      const c = counterpartyOf(loan);
      if (c.id) map.set(c.id, c.name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loans, user?.id]);
  const visibleLoans = (loans ?? []).filter(
    (loan) => !counterpartyId || counterpartyOf(loan).id === counterpartyId,
  );

  // The Dashboard "Loan" quick action links here with ?new=1 to open the modal.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (isLoading) return <Spinner />;

  // Always let the accepter pick their account (lender's disburse / borrower's
  // receive) so money flows to/from the right place on activation.
  const accept = (loan: Loan): void => setAcceptFor(loan);

  const reject = async (loan: Loan): Promise<void> => {
    const ok = await confirm({
      title: "Decline this loan?",
      confirmLabel: "Decline",
      danger: true,
    });
    if (!ok) return;
    await respond
      .mutateAsync({ id: loan.id, accept: false })
      .then(() => toast.success("Declined"))
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
  };

  const declineRepayment = async (loan: Loan): Promise<void> => {
    if (!loan.pendingRepayment) return;
    const ok = await confirm({ title: "Decline this repayment?", confirmLabel: "Decline", danger: true });
    if (!ok) return;
    await respondRepayment
      .mutateAsync({ repaymentId: loan.pendingRepayment.id, accept: false })
      .then(() => toast.success("Repayment declined"))
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
  };

  const undo = async (loan: Loan): Promise<void> => {
    const ok = await confirm({
      title: "Undo this loan?",
      message: "The other party (and any witnesses) must approve before it's reversed.",
      confirmLabel: "Request undo",
      danger: true,
    });
    if (!ok) return;
    await requestUndo
      .mutateAsync({ id: loan.id })
      .then((r: { status: string }) =>
        toast.success(r.status === "cancelled" ? "Loan undone" : "Undo request sent for approval"),
      )
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
  };

  const answerUndo = async (loan: Loan, accept: boolean): Promise<void> => {
    const ok = await confirm({
      title: accept ? "Approve undo?" : "Keep this loan?",
      message: accept ? "This reverses the loan and restores both balances." : undefined,
      confirmLabel: accept ? "Approve undo" : "Keep loan",
      danger: accept,
    });
    if (!ok) return;
    await respondUndo
      .mutateAsync({ id: loan.id, accept })
      .then(() => toast.success(accept ? "Loan undone" : "Undo declined"))
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-2xl font-bold text-brand-500 sm:text-3xl">Loans</h1>
        <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-[auto_minmax(10rem,1fr)_auto] lg:w-auto">
          <PeriodFilter value={period} onChange={setPeriod} />
          {counterparties.length > 0 && (
            <select
              aria-label="Person"
              className="min-w-0 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              value={counterpartyId}
              onChange={(e) => setCounterpartyId(e.target.value)}
            >
              <option value="">Everyone</option>
              {counterparties.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <Button className="min-h-10 w-full whitespace-nowrap sm:w-auto" onClick={() => setOpen(true)} disabled={accounts.length === 0}>
            <Plus size={16} /> New loan
          </Button>
        </div>
      </div>

      {visibleLoans.length > 0 ? (
        <ul className="space-y-3 sm:space-y-0 sm:divide-y sm:divide-slate-100 sm:overflow-hidden sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-white sm:px-5 sm:shadow-sm">
            {visibleLoans.map((loan) => {
              const lent = loan.lenderUserId === user?.id;
              const isInitiator = loan.initiatorUserId === user?.id;
              const awaitingMe = loan.status === "pending" && !isInitiator && !loan.acceptedAt;
              const pr = loan.pendingRepayment;
              const repayAwaitingMe = Boolean(pr && pr.initiatorUserId !== user?.id);
              const undoAwaitingMe =
                loan.status === "undo_pending" && loan.undoRequestedByUserId !== user?.id;
              const canRepay = loan.status === "active" || loan.status === "overdue";
              const canUndo = canRepay && loan.pendingRepayments.length === 0 && loan.outstanding === loan.principal;
              const counterparty = lent ? loan.borrowerName : loan.lenderName;
              return (
                <li key={loan.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:py-4 sm:shadow-none">
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <button className="min-w-0 flex-1 text-left" onClick={() => navigate(`/loans/${loan.id}`)}>
                      <p className="break-words text-sm font-semibold leading-5 text-slate-700 hover:text-blue-600 sm:truncate">
                        {lent ? "You lent" : "You borrowed from"} {counterparty ?? "someone"}
                        {loan.description ? ` · ${loan.description}` : ""}
                      </p>
                      <p className="mt-0.5 text-xs leading-5 text-slate-400">
                        <StatusLabel loan={loan} isInitiator={isInitiator} meId={user?.id} />
                        {loan.dueDate && ` · due ${new Date(loan.dueDate).toLocaleDateString()}`}
                      </p>
                      {loan.pendingRepayments.length > 0 && (
                        <p className="mt-1 text-xs font-medium leading-5 text-amber-600">
                          {loan.pendingRepayments.length} repayment{loan.pendingRepayments.length === 1 ? "" : "s"} awaiting confirmation
                        </p>
                      )}
                    </button>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold text-brand-500">
                        {formatMoney(loan.outstanding, loan.currency)}
                      </p>
                      <p className="text-xs text-slate-400">
                        of {formatMoney(loan.principal, loan.currency)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
                    {awaitingMe && (
                      <>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => accept(loan)}>
                          <Check size={15} /> Accept
                        </Button>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => void reject(loan)}>
                          <X size={15} /> Decline
                        </Button>
                      </>
                    )}
                    {repayAwaitingMe && (
                      <>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => setConfirmRepayFor(loan)}>
                          <Check size={15} /> Confirm
                        </Button>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => void declineRepayment(loan)}>
                          <X size={15} /> Decline
                        </Button>
                      </>
                    )}
                    {undoAwaitingMe && (
                      <>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => void answerUndo(loan, true)}>
                          Approve undo
                        </Button>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => void answerUndo(loan, false)}>
                          Keep
                        </Button>
                      </>
                    )}
                    {canRepay && (
                      // Either party can propose a repayment; the other confirms it.
                      <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => setRepayFor(loan)}>
                        {lent ? "Record repayment" : "Repay"}
                      </Button>
                    )}
                    {canUndo && (
                      <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" onClick={() => void undo(loan)}>
                        Undo
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
        </ul>
      ) : (
        <Card className="p-4 sm:p-5">
          <p className="text-sm text-slate-500">No loans in this period.</p>
        </Card>
      )}

      {open && (
        <CreateLoanModal
          accounts={accounts}
          busy={createLoan.isPending}
          onClose={() => setOpen(false)}
          onSubmit={async (vals) => {
            try {
              const res = await createLoan.mutateAsync(vals);
              toast.success(
                res.status === "pending" ? "Loan request sent for approval" : "Loan created",
              );
              setOpen(false);
            } catch (e) {
              toast.error(e instanceof Error ? e.message : "Failed to create loan");
            }
          }}
        />
      )}

      {acceptFor && (
        <AcceptWithAccountModal
          amLender={acceptFor.lenderUserId === user?.id}
          accounts={accounts}
          busy={respond.isPending}
          onClose={() => setAcceptFor(null)}
          onReject={async () => {
            await respond
              .mutateAsync({ id: acceptFor.id, accept: false })
              .then(() => toast.success("Declined"))
              .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
            setAcceptFor(null);
          }}
          onSubmit={async (accountId) => {
            await respond
              .mutateAsync({ id: acceptFor.id, accept: true, accountId })
              .then(() => toast.success("Loan accepted"))
              .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
            setAcceptFor(null);
          }}
        />
      )}

      {repayFor && (
        <RepayModal
          loan={repayFor}
          amLender={repayFor.lenderUserId === user?.id}
          accounts={accounts}
          busy={repay.isPending}
          onClose={() => setRepayFor(null)}
          onSubmit={async (accountId, amountMinor, paidAt) => {
            await repay
              .mutateAsync({ id: repayFor.id, accountId, amountMinor, paidAt })
              .then((r: { status: string }) =>
                toast.success(
                  r.status === "accepted"
                    ? "Repayment recorded"
                    : "Repayment sent for confirmation",
                ),
              )
              .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
            setRepayFor(null);
          }}
        />
      )}

      {confirmRepayFor?.pendingRepayment && (
        <ConfirmRepaymentModal
          amLender={confirmRepayFor.lenderUserId === user?.id}
          amount={confirmRepayFor.pendingRepayment.amount}
          currency={confirmRepayFor.currency}
          accounts={accounts}
          busy={respondRepayment.isPending}
          onClose={() => setConfirmRepayFor(null)}
          onSubmit={async (accountId) => {
            const repaymentId = confirmRepayFor.pendingRepayment!.id;
            await respondRepayment
              .mutateAsync({ repaymentId, accept: true, accountId })
              .then(() => toast.success("Repayment confirmed"))
              .catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
            setConfirmRepayFor(null);
          }}
        />
      )}
    </div>
  );
}

function StatusLabel({
  loan,
  isInitiator,
  meId,
}: {
  loan: Loan;
  isInitiator: boolean;
  meId?: string;
}): JSX.Element {
  if (loan.status === "pending") {
    if (!loan.acceptedAt) {
      return <>{isInitiator ? "Pending — awaiting the other party" : "Pending — your approval"}</>;
    }
    return <>Pending — awaiting witnesses</>;
  }
  if (loan.status === "undo_pending") {
    const mine = loan.undoRequestedByUserId === meId;
    return <>{mine ? "Undo pending — awaiting the other party" : "Undo pending — your approval"}</>;
  }
  if (loan.pendingRepayment) {
    const mine = loan.pendingRepayment.initiatorUserId === meId;
    return <>{mine ? "Repayment pending — awaiting the other party" : "Repayment pending — your confirmation"}</>;
  }
  return <>{loan.status}</>;
}

export function CreateLoanModal({
  accounts,
  busy,
  onClose,
  onSubmit,
}: {
  accounts: Account[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (vals: CreateLoanBody) => Promise<void>;
}): JSX.Element {
  const { user } = useAuth();
  const { data: connections } = useConnections();
  const [direction, setDirection] = useState<"lent" | "borrowed">("lent");
  const [via, setVia] = useState<"connection" | "email">("connection");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(user?.defaultCurrency ?? DEFAULT_CURRENCY);
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [requiresWitness, setRequiresWitness] = useState(false);
  const [witnessEmails, setWitnessEmails] = useState("");
  const [rateOverride, setRateOverride] = useState("");
  const [error, setError] = useState<string | null>(null);

  const accepted = connections?.connections ?? [];
  const acctCurrency = accounts.find((a) => a.id === accountId)?.currency;
  const crossCurrency = Boolean(acctCurrency && acctCurrency !== currency);
  const { data: liveRate } = useFxRate(currency, acctCurrency ?? currency);

  // Prefill the editable rate from the live rate when the pair changes.
  useEffect(() => {
    if (crossCurrency && liveRate) setRateOverride(String(liveRate));
  }, [crossCurrency, liveRate]);

  return (
    <Modal open title="New loan" onClose={onClose}>
      <div className="space-y-3">
        <Select
          label="Direction"
          value={direction}
          onChange={(e) => setDirection(e.target.value as "lent" | "borrowed")}
        >
          <option value="lent">I'm lending money</option>
          <option value="borrowed">I'm borrowing money</option>
        </Select>

        <Select label="Counterparty by" value={via} onChange={(e) => setVia(e.target.value as "connection" | "email")}>
          <option value="connection">A connection</option>
          <option value="email">Email (off-platform)</option>
        </Select>

        {via === "connection" ? (
          <Select label="Connection" value={username} onChange={(e) => setUsername(e.target.value)}>
            <option value="">Select…</option>
            {accepted.map((c) => (
              <option key={c.id} value={c.user.username ?? ""}>
                {c.user.name} {c.user.username ? `(@${c.user.username})` : ""}
              </option>
            ))}
          </Select>
        ) : (
          <Input label="Counterparty email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        )}

        <Select
          label={direction === "lent" ? "Lend from account" : "Receive into account (optional)"}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          {direction === "borrowed" && <option value="">Don't track in an account</option>}
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-3 gap-2">
          <Input className="col-span-2" label="Amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Select label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
        </div>

        {crossCurrency && (
          <div className="rounded-lg bg-slate-50 p-3">
            <Input
              label={`Rate ${currency} → ${acctCurrency} (editable)`}
              type="number"
              step="0.0001"
              value={rateOverride}
              onChange={(e) => setRateOverride(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-400">
              {amount && rateOverride
                ? `${amount} ${currency} ≈ ${(Number(amount) * Number(rateOverride)).toFixed(2)} ${acctCurrency} in the account`
                : "Live rate prefilled — adjust if you agreed a different one."}
            </p>
          </div>
        )}

        <Input label="Due date (optional)" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        <Input label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />

        <div className="rounded-lg border border-slate-200 p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={requiresWitness}
              onChange={(e) => setRequiresWitness(e.target.checked)}
            />
            Require witnesses to approve
          </label>
          {requiresWitness && (
            <Input
              className="mt-2"
              label="Witness emails (comma-separated)"
              value={witnessEmails}
              onChange={(e) => setWitnessEmails(e.target.value)}
              placeholder="friend@example.com"
            />
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              const principalMinor = toMinorUnits(Number(amount));
              if (principalMinor <= 0) return setError("Enter a positive amount.");
              if (via === "connection" && !username) return setError("Pick a connection.");
              if (via === "email" && !email) return setError("Enter the counterparty email.");
              if (direction === "lent" && !accountId) return setError("Pick an account to lend from.");
              const witnesses = witnessEmails
                .split(",")
                .map((e) => e.trim())
                .filter(Boolean)
                .map((e) => ({ email: e }));
              void onSubmit({
                direction,
                ...(via === "connection" ? { counterpartyUsername: username } : { counterpartyEmail: email }),
                accountId: accountId || undefined,
                principalMinor,
                currency,
                dueDate: dueDate || undefined,
                description: description || undefined,
                requiresWitness,
                witnesses: requiresWitness && witnesses.length > 0 ? witnesses : undefined,
                fxRate: crossCurrency && Number(rateOverride) > 0 ? Number(rateOverride) : undefined,
              });
            }}
          >
            {busy ? "Saving…" : "Send request"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AcceptWithAccountModal({
  amLender,
  accounts,
  busy,
  onClose,
  onReject,
  onSubmit,
}: {
  amLender: boolean;
  accounts: { id: string; name: string }[];
  busy: boolean;
  onClose: () => void;
  onReject: () => Promise<void>;
  onSubmit: (accountId?: string) => Promise<void>;
}): JSX.Element {
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  // Lender must pick the account money leaves; borrower optionally picks where to receive it.
  return (
    <Modal open title={amLender ? "Accept & lend from" : "Accept loan"} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">
          {amLender
            ? "Choose the account the money will be lent from (it will be debited)."
            : "Optionally choose the account to receive the money into."}
        </p>
        <Select
          label={amLender ? "Lend from account" : "Receive into account (optional)"}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          {!amLender && <option value="">Don't track in an account</option>}
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <div className="flex justify-between gap-2 pt-2">
          <Button variant="ghost" onClick={() => void onReject()} disabled={busy}>
            Decline
          </Button>
          <Button
            disabled={busy || (amLender && !accountId)}
            onClick={() => void onSubmit(accountId || undefined)}
          >
            {busy ? "Saving…" : "Accept loan"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RepayModal({
  loan,
  amLender,
  accounts,
  busy,
  onClose,
  onSubmit,
}: {
  loan: Loan;
  amLender: boolean;
  accounts: { id: string; name: string }[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (accountId: string, amountMinor: number, paidAt: string) => Promise<void>;
}): JSX.Element {
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(() => toDateTimeLocal(new Date()));

  return (
    <Modal open title={amLender ? "Record a repayment" : "Repay loan"} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">
          {amLender
            ? "Record money the borrower paid back to you. They'll confirm before it's posted — then it's credited to the account below."
            : "Tell the lender you've paid them back. They confirm before it posts — then it's debited from the account below."}
        </p>
        <p className="text-sm text-slate-500">
          Outstanding: {formatMoney(loan.outstanding, loan.currency)}
        </p>
        <Select
          label={amLender ? "Receive into account" : "Pay from account"}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <Input label="Amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input label="Paid on" type="datetime-local" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              const amountMinor = toMinorUnits(Number(amount));
              if (accountId && amountMinor > 0 && paidAt) {
                void onSubmit(accountId, amountMinor, new Date(paidAt).toISOString());
              }
            }}
          >
            {busy ? "Saving…" : "Send for confirmation"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function toDateTimeLocal(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

/** The counterparty confirms a proposed repayment, naming their own account. */
function ConfirmRepaymentModal({
  amLender,
  amount,
  currency,
  accounts,
  busy,
  onClose,
  onSubmit,
}: {
  amLender: boolean;
  amount: number;
  currency: string;
  accounts: { id: string; name: string }[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (accountId?: string) => Promise<void>;
}): JSX.Element {
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  // The lender confirms receipt (credited); the borrower confirms they paid (debited).
  return (
    <Modal open title="Confirm repayment" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">
          Confirm the repayment of <strong>{formatMoney(amount, currency)}</strong>.{" "}
          {amLender
            ? "It will be credited to the account you choose."
            : "It will be debited from the account you choose."}
        </p>
        <Select
          label={amLender ? "Receive into account" : "Pay from account"}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          {!amLender && <option value="">Don't track in an account</option>}
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy || (amLender && !accountId)} onClick={() => void onSubmit(accountId || undefined)}>
            {busy ? "Saving…" : "Confirm repayment"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
